import React from 'react';
import { Icon, Tooltip, Typography } from 'antd';
import moment from 'moment';
import DateCellContent from '../../shared/ui/DateCellContent/DateCellContent';
import {
  TableContainer,
  TableBody,
  TableBodyRow,
  TableHead,
  TableHeadCell,
} from '../../../../components/tableComponents';
import { sortAndFilterData } from '../../features/table';
import {
  changeShutdownPlanEndDate,
  changeShutdownEndDate,
  changeShutdownType,
  changeShutdownReason,
  changeShutdownInitiators,
  changeShutdownResponsible,
  changeShutdownDesc,
} from '../../features/shutdown';
import WorkerCellContent from '../../shared/ui/WorkerCellContent/WorkerCellContent';

const { ShutdownPlanEndDateContent } = changeShutdownPlanEndDate;
const { ShutdownEndDateContent } = changeShutdownEndDate;
const { ShutdownTypeContent } = changeShutdownType;
const { ShutdownReasonContent } = changeShutdownReason;
const { ShutdownInitiatorsContent } = changeShutdownInitiators;
const { ShutdownResponsibleContent } = changeShutdownResponsible;
const { ShutdownDescContent } = changeShutdownDesc;

const { Text } = Typography;
const { sortOrder, sorters } = sortAndFilterData;

export const selectionCellWidth = '32px';
export const quickJumpSize = 9;
export const rowCounts = [10, 30, 50, 100];

export const components = {
  table: (props) => (
    <TableContainer
      {...props}
      removePadding
    />
  ),
  header: {
    wrapper: TableHead,
    cell: TableHeadCell,
  },
  body: {
    wrapper: TableBody,
    row: ({ colCount, record, expandedRowRender, expanded, ...props }) => (
      <TableBodyRow
        {...props}
        isExpanded={expanded}
        isClickable
        extraRowProps={{
          expandedRowRender,
          colCount,
          hasEmptyCell: true,
          record,
        }}
      />
    ),
  },
};

export const columnKeys = {
  number: 'number',
  isPlaying: 'isPlaying',
  startDate: 'startDate',
  planEndDate: 'planEndDate',
  typeName: 'typeName',
  endDate: 'endDate',
  reasonName: 'reasonName',
  initiator: 'initiator',
  responsible: 'responsible',
  createWorker: 'createWorker',
  createDate: 'createDate',
  description: 'description',
  serviceCompany: 'serviceCompany.name',
};

export const columns = [
  {
    title: '№',
    dataIndex: 'alertId',
    key: columnKeys.number,
    sorter: sorters.number('alertId'),
    getSortOrder: (sortedInfo) => sortOrder(sortedInfo, columnKeys.number),
    render: (text) => (
      <Text style={{ color: '#09a2f2', fontWeight: 500 }}>
        {text || 'Не указано'}
      </Text>
    ),
  },

  {
    title: 'Проигрывание',
    dataIndex: 'isPlaying',
    key: columnKeys.isPlaying,
    sorter: sorters.boolean('isPlaying'),
    getSortOrder: (sortedInfo) => sortOrder(sortedInfo, columnKeys.isPlaying),
    render: (isPlaying) => {
      if (!isPlaying) return null;

      return (
        <Icon
          type="play-circle"
          style={{ color: '#09a2f2', fontSize: 24 }}
        />
      );
    },
  },

  {
    title: 'Начало',
    dataIndex: 'startDate',
    key: columnKeys.startDate,
    sorter: sorters.date('startDate'),
    getSortOrder: (sortedInfo) => sortOrder(sortedInfo, columnKeys.startDate),
    render: (_, shutdown) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Tooltip title="Дата начала">
          <DateCellContent
            date={shutdown?.startDate ? moment(shutdown?.startDate) : undefined}
          />
        </Tooltip>
        <Tooltip title="Дата создания">
          <DateCellContent
            date={
              shutdown?.createDate ? moment(shutdown?.createDate) : undefined
            }
          />
        </Tooltip>
      </div>
    ),
  },

  {
    title: 'Завершение',
    dataIndex: 'planEndDate',
    key: columnKeys.planEndDate,
    sorter: sorters.date('planEndDate'),
    getSortOrder: (sortedInfo) => sortOrder(sortedInfo, columnKeys.planEndDate),
    render: (_, shutdown) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <ShutdownPlanEndDateContent shutdown={shutdown} />
        <ShutdownEndDateContent shutdown={shutdown} />
      </div>
    ),
  },

  {
    title: 'Тип',
    dataIndex: 'alertType.name',
    key: columnKeys.typeName,
    sorter: sorters.string('alertType.name'),
    getSortOrder: (sortedInfo) => sortOrder(sortedInfo, columnKeys.typeName),
    render: (_, shutdown) => <ShutdownTypeContent shutdown={shutdown} />,
    filterMultiple: false,
  },

  {
    title: 'Причина',
    dataIndex: 'alertReason.name',
    key: columnKeys.reasonName,
    sorter: sorters.string('alertReason.name'),
    getSortOrder: (sortedInfo) => sortOrder(sortedInfo, columnKeys.reasonName),
    render: (_, shutdown) => <ShutdownReasonContent shutdown={shutdown} />,
  },

  {
    title: 'Описание',
    dataIndex: 'description',
    key: columnKeys.description,
    render: (_, shutdown) => (
      <ShutdownDescContent
        shutdown={shutdown}
        description={shutdown.description?.trim()}
      />
    ),
  },

  ({ expandedRowKeys }) => ({
    title: 'Инициаторы',
    dataIndex: 'initiators',
    key: columnKeys.initiator,
    render: (_, shutdown) => (
      <ShutdownInitiatorsContent
        shutdown={shutdown}
        rowExpanded={expandedRowKeys.includes(shutdown.alertId)}
      />
    ),
  }),

  ({ expandedRowKeys }) => ({
    title: 'Ответственные',
    dataIndex: 'executors',
    key: columnKeys.responsible,
    render: (_, shutdown) => (
      <ShutdownResponsibleContent
        shutdown={shutdown}
        rowExpanded={expandedRowKeys.includes(shutdown.alertId)}
      />
    ),
  }),

  {
    title: 'Создатель',
    key: columnKeys.createWorker,
    sorter: sorters.string('createAlertWorker.surName'),
    getSortOrder: (sortedInfo) =>
      sortOrder(sortedInfo, columnKeys.createWorker),
    render: (_, record) => (
      <WorkerCellContent
        worker={record.createAlertWorker}
        style={{ maxWidth: '150px' }}
      />
    ),
  },

  ({ expandedRowKeys }) => ({
    title: 'УК',
    dataIndex: columnKeys.serviceCompany,
    key: columnKeys.serviceCompany,
    sorter: sorters.string(columnKeys.serviceCompany),
    getSortOrder: (sortedInfo) =>
      sortOrder(sortedInfo, columnKeys.serviceCompany),
    render: (_, shutdown) => (
      <Text style={{ fontWeight: 500 }}>
        {shutdown?.serviceCompany?.name || 'Не указана'}
      </Text>
    ),
  }),
];
